import { Link } from 'react-router-dom';
import GuideLayout from '../../components/guides/GuideLayout';
import GuideCTA from '../../components/guides/GuideCTA';
import { GuideSection, GuideFaqItem, GuideChecklist, GuideNote, SourceLink } from '../../components/guides/GuideParts';

const SLUG = 'how-to-request-your-care-records-from-the-council';

export const RequestCareRecords = () => (
  <GuideLayout slug={SLUG}>
    <GuideSection>
      <p>
        If you spent any time in the care of a local authority — in foster care, a children&apos;s
        home, kinship care, or under a care order while living with relatives — that council
        holds a social care file on you. It is usually the single richest record of your
        childhood that exists anywhere: assessments, court reports, review minutes, contact
        notes, and the day-to-day observations of the social workers involved in your life.
      </p>
      <p>
        You have a legal right to see it, under Article 15 of the UK GDPR, by sending a Subject
        Access Request (SAR) — see our guide on{' '}
        <Link to="/guides/what-is-a-subject-access-request" className="text-brand underline hover:opacity-90">
          what a SAR is
        </Link>{' '}
        if you want the fuller explanation. This guide covers what is specific to a council.
      </p>
    </GuideSection>

    <GuideSection eyebrow="Working out who to ask" title="Which council was responsible for you?">
      <p>
        It is the council that held statutory responsibility for you at the time — sometimes
        called your "corresponding authority" if you have since moved area — not necessarily the
        one you live in now. A few ways to work this out if you are not sure:
      </p>
      <GuideChecklist
        items={[
          'Think about where you were living when you first became involved with a social worker — that local authority is usually the right one, even if you were later placed with a foster carer somewhere else.',
          'If you moved between areas while in care, the original council usually kept responsibility. It is common to have been placed by one council but living in another area entirely.',
          'A birth certificate, an old letter, or a school you remember attending can help narrow down the area, even if you don\'t know the exact team or office.',
          'If you genuinely don\'t know, it is fine to ask the council that covers where you believe you lived — they can redirect you, or search their own historic records for your name.',
        ]}
      />
    </GuideSection>

    <GuideSection eyebrow="What to ask for" title="Be specific — 'my file' is not enough">
      <p>
        Councils hold care records across several different systems and teams, so a vague
        request is more likely to come back incomplete. Ask specifically for:
      </p>
      <GuideChecklist
        items={[
          'Your full social work case file, including case notes, assessments and chronologies.',
          'Looked After Child (LAC) reviews and minutes of any meetings held about you.',
          'Any court reports, care plans or placement plans.',
          'Correspondence about you between social workers, foster carers, schools or health services.',
          'Any life story work or memory book material held by the local authority (not just what was given to you at the time).',
          'Records of any complaints, allegations or safeguarding concerns involving you.',
        ]}
      />
      <p>
        If you remember the names of specific social workers, IROs (Independent Reviewing
        Officers) or team managers, include them — it helps the council search efficiently, and
        you are entitled to see what any of them wrote about you.
      </p>
    </GuideSection>

    <GuideNote>
      <p>
        <strong>Previous names matter.</strong> If you were known by a different surname during
        part of your childhood — a foster carer&apos;s name, for instance — mention it. Records are
        often filed under the name used at the time, and a council that only searches your
        current name can miss whole periods of your file.
      </p>
    </GuideNote>

    <GuideCTA />

    <GuideSection eyebrow="What happens next" title="Timeline and what to expect">
      <p>
        The council has <strong className="text-ink">one calendar month</strong> to respond,
        extendable by up to two further months for a complex request — a full childhood case
        file across several placements very often qualifies, so don&apos;t be surprised by an
        extension, as long as they tell you about it within the first month and explain why.
      </p>
      <p>
        Expect some parts of the file to arrive redacted — with names or paragraphs blacked out.
        This is sometimes lawful (see our{' '}
        <Link to="/guides/understanding-redactions-in-your-care-records" className="text-brand underline hover:opacity-90">
          guide to redactions
        </Link>
        ) but it must be justified case by case, not applied as a blanket policy. You can ask the
        council to explain any redaction you don&apos;t understand.
      </p>
    </GuideSection>

    <GuideSection eyebrow="Common questions" title="Questions people ask us most" tinted>
      <div className="space-y-2" data-testid="council-records-faq">
        <GuideFaqItem
          q="I was adopted from care. Should I ask the council or the adoption agency?"
          a="Both hold different things. The council usually holds records from before the adoption order was made — the original care proceedings and any pre-adoption placements. The adoption agency (which may be the council's own adoption team, a voluntary agency, or a regional adoption agency) holds the adoption-specific file. See our adoption records guide for that side of it."
        />
        <GuideFaqItem
          q="What if the council says it no longer holds my file?"
          a="Ask them to confirm the date it was destroyed and the retention policy that allowed it. Local authority care records typically have very long statutory retention — commonly until you turn 75 — so an early destruction date is worth questioning, and worth raising with the ICO if the explanation doesn't add up."
        />
        <GuideFaqItem
          q="Can I get records about my birth family's involvement, like why I was removed?"
          a={
            <>
              Yes, records explaining decisions made about you — including the reasons recorded
              for your removal into care — are your personal data and should be included.
              Information solely and specifically about someone else, with nothing to do with
              you, may be withheld or redacted; the reasoning behind decisions made about your
              life generally is not. See our guide on{' '}
              <Link
                to="/guides/how-to-find-out-why-you-were-taken-into-care"
                className="text-brand underline hover:opacity-90"
              >
                how to find out why you were taken into care
              </Link>{' '}
              for exactly which documents to ask for.
            </>
          }
        />
        <GuideFaqItem
          q="I'm still in care now. Can I ask for my own file?"
          a="Yes, at any age — it's your file and your right. If you're under 18 you may want a social worker, advocate or trusted adult to help you through the request, but you don't need anyone's permission to make it."
        />
      </div>
    </GuideSection>

    <GuideSection eyebrow="Read more" title="Where this comes from">
      <p>
        The ICO&apos;s detailed guidance on the{' '}
        <SourceLink href="https://ico.org.uk/for-organisations/uk-gdpr-guidance-and-resources/individual-rights/right-of-access/">
          right of access
        </SourceLink>{' '}
        covers timescales, exemptions and identity checks in full. The ICO also runs{' '}
        <SourceLink href="https://ico.org.uk/about-the-ico/campaigns/better-records-together/">
          Better Records Together
        </SourceLink>
        , a project specifically about care leavers accessing their records well.
      </p>
    </GuideSection>

    <GuideCTA
      heading="We can send this one for you"
      body="Tell us which council, and what you remember about your placements. We'll draft a letter that asks for everything above, send it, and track the deadline so you don't have to keep checking."
      testIdPrefix="guide-cta-end"
    />
  </GuideLayout>
);

export default RequestCareRecords;
